import type { RendererPreference } from '../project/ProjectDocument.js'
import type { RenderBackend } from './RenderBackend.js'
import { createCanvas2DBackend } from './canvas/Canvas2DBackend.js'
import { createPixiParticleBackend } from './webgl/PixiParticleBackend.js'

export interface CreatedRenderBackend {
  readonly canvas: RenderCanvas
  readonly backend: RenderBackend
  createCanvasFallback(): CreatedRenderBackend
}

export interface RenderCanvas {
  width: number
  height: number
  getContext(kind: string, options?: unknown): unknown
  addEventListener(type: string, listener: (event: Event) => void): void
  removeEventListener(type: string, listener: (event: Event) => void): void
}

export interface RenderBackendFactoryDependencies {
  createCanvas(): RenderCanvas
  getWebGL2(canvas: RenderCanvas): unknown
  isWebGL2(context: unknown): boolean
  createCanvas2D(canvas: RenderCanvas): RenderBackend
  createWebGL2(canvas: RenderCanvas, context: WebGL2RenderingContext): Promise<RenderBackend>
}

export interface CreateRenderBackendOptions {
  readonly preferred: RendererPreference
  readonly canvas2dPresentation?: RenderCanvas
  readonly dependencies?: RenderBackendFactoryDependencies
}

function productionDependencies(): RenderBackendFactoryDependencies {
  return {
    createCanvas: () => document.createElement('canvas'),
    getWebGL2: (canvas) => canvas.getContext('webgl2', {
      alpha: true,
      antialias: true,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    }),
    isWebGL2: (context) => {
      const Constructor = globalThis.WebGL2RenderingContext
      return typeof Constructor === 'function' && context instanceof Constructor
    },
    createCanvas2D: (canvas) => createCanvas2DBackend(canvas as HTMLCanvasElement),
    createWebGL2: (canvas, context) => createPixiParticleBackend(canvas as HTMLCanvasElement, context),
  }
}

function createCanvasHandle(
  dependencies: RenderBackendFactoryDependencies,
  presentation?: RenderCanvas,
): CreatedRenderBackend {
  const canvas = presentation ?? dependencies.createCanvas()
  const backend = dependencies.createCanvas2D(canvas)
  if (backend.kind !== 'canvas2d' || backend.getStatus() !== 'ready') {
    backend.dispose()
    throw new Error('Canvas backend factory must return a ready Canvas2D backend')
  }
  return {
    canvas,
    backend,
    createCanvasFallback: () => createCanvasHandle(dependencies, presentation),
  }
}

export async function createRenderBackend(options: CreateRenderBackendOptions): Promise<CreatedRenderBackend> {
  const dependencies = options.dependencies ?? productionDependencies()
  if (options.preferred === 'canvas2d') {
    return createCanvasHandle(dependencies, options.canvas2dPresentation)
  }
  if (options.preferred !== 'webgl2') throw new RangeError(`Unknown renderer preference: ${String(options.preferred)}`)

  const candidateCanvas = dependencies.createCanvas()
  const candidateContext = dependencies.getWebGL2(candidateCanvas)
  if (candidateContext === null || !dependencies.isWebGL2(candidateContext)) {
    return createCanvasHandle(dependencies, options.canvas2dPresentation)
  }

  try {
    const backend = await dependencies.createWebGL2(
      candidateCanvas,
      candidateContext as WebGL2RenderingContext,
    )
    if (backend.kind !== 'webgl2' || backend.getStatus() !== 'ready') {
      backend.dispose()
      return createCanvasHandle(dependencies, options.canvas2dPresentation)
    }
    return {
      canvas: candidateCanvas,
      backend,
      createCanvasFallback: () => createCanvasHandle(dependencies, options.canvas2dPresentation),
    }
  } catch {
    return createCanvasHandle(dependencies, options.canvas2dPresentation)
  }
}
